import type { CarUpgrade, RenderMode, VehicleBlueprint, WeaponUpgrade } from "@neon-arena/shared";
import { ClientNetwork, type LobbySettingsPayload } from "../../networking/ClientNetwork.js";
import type { Game } from "../../game/Game.js";
import type { ControlMethod } from "../VehicleSelect.js";
import { LobbyMachine, shellVisibleFor } from "../lobbyState.js";
import { announcer } from "../announcer.js";
import { matchAnnouncer } from "../announcerMatch.js";

/**
 * LobbyFlow — the one owner of the hosted-lobby session on the client
 * (docs/menu-redesign-plan.md §3.5, server contract in HostedArenaRoom.ts).
 *
 * Two ScreenManager screens (LobbyLandingScreen, LobbyRoomScreen) share a
 * single network connection and a single {@link LobbyMachine}; this class
 * holds both and the Game-boot orchestration, so the screens stay thin
 * projections — the same split QuickMatchScreen has, spread over two screens.
 *
 * ── Deferred spawn (mirror of the quickmatch flow) ─────────────────────────
 * `createHosted`/`joinHostedByCode` join the room WITHOUT spawning; `hello`
 * (with `preferLeader` on the host path) puts us on the roster. The local
 * `Game` is booted exactly once, when the server broadcasts `start` for a
 * round we are in. Rematches and lobby-returns reuse the running Game — the
 * server just re-seats us and resets the arena around it, exactly like the
 * queue room's winner-stays-on rounds.
 *
 * ── Shell choreography ─────────────────────────────────────────────────────
 * Shell visibility is a projection of the model (`shellVisibleFor`): visible
 * for lobby/post/errors, hidden for a live round. This class watches the
 * projection flip and calls the deps that main.ts wires to the existing
 * leaveShellForOverlay / backToShell helpers.
 */

/** The loadout snapshot a lobby session plays with. Same fields quickmatch uses. */
export interface LobbyLaunch {
  serverUrl: string;
  driverName: string;
  blueprint: VehicleBlueprint;
  upgrades: CarUpgrade;
  weaponUpgrades: WeaponUpgrade;
  controlMethod: ControlMethod;
  renderMode: RenderMode;
  authToken: string | null;
}

export interface LobbyFlowDeps {
  game: Game;
  /**
   * Pull the player's current loadout off the existing lobby state
   * (main.ts intercepts `select.confirmQuickMatch()` — synchronous).
   * Null when no vehicle is selectable.
   */
  requestLaunch: () => LobbyLaunch | null;
  /** `screens.show("lobby-room")` */
  showRoom: () => void;
  /** `screens.show("lobby-landing")` */
  showLanding: () => void;
  /** Hide the shell for live play (main.ts onMatchEnter: sets the return key + hideAll). */
  hideShellForMatch: () => void;
  /** Bring the shell back over the still-running game (`backToShell("lobby-room")`). */
  showShellOverMatch: () => void;
  /** Bail out to the main menu (`backToShell("main-menu")`). */
  exitToMenu: () => void;
  /** Transient notice on the main menu (mainMenu.setNotice). */
  notify?: (msg: string) => void;
}

/** Trailing debounce for leader settings sends — must exceed the server's 100 ms control throttle. */
const CONFIG_SEND_DELAY_MS = 160;

export class LobbyFlow {
  readonly machine = new LobbyMachine();

  private net: ClientNetwork | null = null;
  private launch: LobbyLaunch | null = null;
  private gameBooted = false;
  /** Bumped on every begin/teardown so a stale async join can never attach. */
  private attemptSeq = 0;
  private listeners: Array<() => void> = [];
  private lastShellVisible = true;
  /** One-shot error strip for the landing screen (bad code, busy room…). */
  private lastNotice: string | null = null;

  private pendingSettings: LobbySettingsPayload | null = null;
  private configTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly deps: LobbyFlowDeps) {}

  // ── Screen surface ────────────────────────────────────────────────────────

  onChange(fn: () => void): void {
    this.listeners.push(fn);
  }

  ownId(): string {
    return this.net?.ownId() ?? "";
  }

  isGameBooted(): boolean {
    return this.gameBooted;
  }

  /** Read-and-clear the landing notice. */
  takeNotice(): string | null {
    const n = this.lastNotice;
    this.lastNotice = null;
    return n;
  }

  /** Leader's optimistic settings view until the server's roster echo lands. */
  effectiveSettings(): LobbySettingsPayload | null {
    return this.pendingSettings ?? this.machine.model().settings;
  }

  // ── Entry points ──────────────────────────────────────────────────────────

  async host(): Promise<void> {
    await this.begin("host", (net, launch) =>
      net.createHosted({
        serverUrl: launch.serverUrl,
        driverName: launch.driverName,
        authToken: launch.authToken,
      }),
    );
  }

  async joinByCode(code: string): Promise<void> {
    await this.begin("join", (net, launch) =>
      net.joinHostedByCode(code, {
        serverUrl: launch.serverUrl,
        driverName: launch.driverName,
        authToken: launch.authToken,
      }),
    );
  }

  private async begin(
    mode: "host" | "join",
    connect: (net: ClientNetwork, launch: LobbyLaunch) => Promise<void>,
  ): Promise<void> {
    if (this.machine.model().phase === "connecting") return; // one attempt at a time
    const launch = this.deps.requestLaunch();
    if (!launch) {
      this.lastNotice = "NO LOADOUT — PICK A VEHICLE FIRST";
      this.deps.showLanding();
      this.emit();
      return;
    }
    this.teardownNet();
    this.launch = launch;
    this.gameBooted = false;
    this.lastShellVisible = true;
    this.machine.begin(mode);
    const seq = ++this.attemptSeq;
    this.deps.showRoom(); // room screen renders the CONNECTING state
    this.emit();

    const net = new ClientNetwork();
    this.wire(net, seq);
    try {
      await connect(net, launch);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      try { net.leave(); } catch { /* never attached */ }
      if (seq !== this.attemptSeq) return; // cancelled meanwhile
      this.lastNotice = msg.toUpperCase();
      this.machine.reset();
      this.deps.showLanding();
      this.emit();
      return;
    }
    if (seq !== this.attemptSeq) {
      // Cancelled while the join was in flight — drop the late room.
      try { net.leave(); } catch { /* noop */ }
      return;
    }
    this.net = net;
    this.machine.connected(net.ownId());
    // `hello` puts us on the roster; the host device stakes its leader claim.
    net.enterMatch({
      driverName: launch.driverName,
      blueprint: launch.blueprint,
      weaponUpgrades: launch.weaponUpgrades,
      preferLeader: mode === "host",
    });
    this.emit();
  }

  /** Back/cancel while the join is still in flight. */
  cancelConnect(): void {
    this.attemptSeq++;
    this.teardownNet();
    this.machine.reset();
    this.deps.showLanding();
    this.emit();
  }

  // ── Room actions ──────────────────────────────────────────────────────────

  toggleReady(): void {
    this.net?.sendReady(); // no payload = server-side toggle
  }

  /** Leader edit: merge, show optimistically, debounce-send the FULL settings. */
  changeSetting(patch: Partial<LobbySettingsPayload>): void {
    const base = this.effectiveSettings();
    if (!base) return;
    this.pendingSettings = { ...base, ...patch };
    if (this.configTimer !== null) clearTimeout(this.configTimer);
    this.configTimer = setTimeout(() => {
      this.configTimer = null;
      if (this.pendingSettings) this.net?.sendLobbyConfig(this.pendingSettings);
    }, CONFIG_SEND_DELAY_MS);
    this.emit();
  }

  start(force = false): void {
    this.machine.clearStartRejected();
    this.net?.sendLobbyStart(force);
    this.emit();
  }

  /** Leader, post phase: run it back. The server treats `lobbyStart` in post as the rematch. */
  rematch(): void {
    this.net?.sendLobbyStart(false);
  }

  /** Leader, post phase: skip the countdown, back to the lobby now. */
  backToLobbyEarly(): void {
    this.net?.sendLobbyReturn();
  }

  /**
   * LEAVE, from any phase. Mirrors QuickMatchScreen.leave(): before the Game
   * boots this is just a room leave (instant, back to the landing screen);
   * after the boot the only clean exit the Game offers is destroy + reload —
   * the return key set at boot time lands the reload on the MAIN MENU.
   */
  leaveRoom(): void {
    this.attemptSeq++;
    if (this.gameBooted) {
      this.teardownNet();
      this.deps.game.destroy();
      window.location.reload();
      return;
    }
    this.teardownNet();
    this.machine.reset();
    this.lastShellVisible = true;
    this.deps.showLanding();
    this.emit();
  }

  // ── Server → model ────────────────────────────────────────────────────────

  private wire(net: ClientNetwork, seq: number): void {
    const guard = (fn: () => void) => (): void => {
      if (seq !== this.attemptSeq) return;
      fn();
      this.afterMessage();
    };
    net.onHostedWelcome((p) => guard(() => {
      this.machine.welcome(p);
      if (p.rejected === "in-progress") {
        // The server force-leaves us right after this — bounce to the landing
        // screen with the reason where the code entry lives.
        this.lastNotice = this.machine.model().errorMessage;
        this.attemptSeq++;
        this.teardownNet();
        this.machine.reset();
        this.deps.showLanding();
      }
    })());
    net.onLobbyCode((p) => guard(() => this.machine.lobbyCode(p))());
    net.onLobbyRoster((p) => guard(() => {
      this.machine.roster(p);
      // Server settings echo — drop the optimistic copy once no send is pending.
      if (this.configTimer === null) this.pendingSettings = null;
    })());
    net.onYouAreLeader(() => guard(() => this.machine.youAreLeader())());
    net.onLeaderLeft((p) => guard(() => this.machine.leaderLeft(p))());
    net.onLobbyStartRejected((p) => guard(() => this.machine.startRejected(p))());
    net.onHostedQueueState((p) => guard(() => this.machine.queueState(p))());
    net.onHostedStart((p) => guard(() => {
      this.machine.start(p);
      // Hosted rooms have no seat grant or countdown phase on the wire —
      // `start` is the whole reveal, so the announcer fires the start pair
      // here: GO (high) with ROUND START queued behind it. The tracker gets
      // the round boundary + local round-clock deadline for time_60/time_10.
      matchAnnouncer.roundStarted(p.config ? Date.now() + p.config.roundSeconds * 1000 : 0);
      announcer.announce("go");
      announcer.announce("round_start");
    })());
    net.onHostedRoundEnd((p) => guard(() => {
      this.machine.roundEnd(p);
      matchAnnouncer.roundEnded();
      const ownId = this.ownId();
      const standings = p.standings ?? [];
      if (ownId && standings.some((s) => s.sessionId === ownId)) {
        announcer.announce("round_end");
        const won = standings[0]?.sessionId === ownId;
        // Sequenced, not interrupted: let the short ROUND END clip land first.
        setTimeout(() => announcer.announce(won ? "victory" : "winner"), 1400);
      }
    })());
    net.onHostedLeave((_code, byUs) => guard(() => {
      if (byUs) return;
      const phase = this.machine.model().phase;
      // Rejected/reset paths already routed; a live Game handles its own drop.
      if (phase === "connecting" || phase === "lobby" || phase === "post") {
        if (!this.gameBooted) this.machine.error("CONNECTION LOST — THE ROOM WENT AWAY");
      }
    })());
  }

  private afterMessage(): void {
    if (this.machine.shouldEnterMatch()) void this.bootGame();
    this.syncShell();
    this.emit();
  }

  /**
   * `start` landed for a round we're in. Boot the Game ONCE; on a rematch the
   * server has already re-seated us and reset the arena — the running Game
   * picks its PlayerState back up (`applyServerForLocal`), nothing to boot.
   */
  private async bootGame(): Promise<void> {
    const launch = this.launch;
    const net = this.net;
    if (!launch || !net) return;
    this.machine.markInGame();
    this.syncShell(); // hides the shell (sets the reload-return key via deps)
    this.emit();
    if (this.gameBooted) return;
    this.gameBooted = true;
    const seq = this.attemptSeq;
    try {
      this.deps.game.setRenderMode(launch.renderMode);
      await this.deps.game.start({
        network: net, // adopt the hosted room — do NOT connect/hello again
        serverUrl: launch.serverUrl,
        driverName: launch.driverName,
        blueprint: launch.blueprint,
        controlMethod: launch.controlMethod,
        upgrades: launch.upgrades,
        weaponUpgrades: launch.weaponUpgrades,
        authToken: launch.authToken,
      });
      if (seq !== this.attemptSeq) return;
      this.syncShell(); // a roundEnd may have landed mid-boot
      this.emit();
    } catch (err) {
      if (seq !== this.attemptSeq) return;
      const msg = err instanceof Error ? err.message : String(err);
      console.error("[lobby] game boot failed:", err);
      this.gameBooted = false;
      try { this.deps.game.resetForRetry(); } catch (e) { console.error("[lobby] resetForRetry failed:", e); }
      this.attemptSeq++;
      this.teardownNet();
      this.machine.reset();
      this.lastShellVisible = true;
      this.deps.notify?.(`LOBBY MATCH FAILED — ${msg}`);
      this.deps.exitToMenu();
      this.emit();
    }
  }

  /** Flip the shell when the model's visibility projection flips (booted Game only). */
  private syncShell(): void {
    const visible = shellVisibleFor(this.machine.model());
    if (visible === this.lastShellVisible) return;
    this.lastShellVisible = visible;
    if (!visible) {
      this.deps.hideShellForMatch();
    } else if (this.gameBooted) {
      this.deps.showShellOverMatch();
    }
  }

  private teardownNet(): void {
    if (this.configTimer !== null) { clearTimeout(this.configTimer); this.configTimer = null; }
    this.pendingSettings = null;
    try { this.net?.leave(); } catch { /* already gone */ }
    this.net = null;
  }

  private emit(): void {
    for (const fn of this.listeners) fn();
  }
}
